import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import HttpClient from 'server/helpers/HttpClient';

interface Auth {
  ticket: string;
  realmId: string;
  entityId: string;
  authToken: string;
}

interface Config {
  portal: string;
}

interface Insight {
  domainId: string;
  token: string;
}

interface ChatAnswerRequest {
  userPrompt: string;
  enabledPaymentMethodList: string[];
  isPartialPaymentEnabled: boolean;
  browser: string;
  device: string;
  isAutopayEligible: boolean;
  isAutopaySetup: boolean;
  hasAchConvenienceFee: boolean;
  achConvenienceFee: number;
  merchantName: string;
}

interface ChatAnswerResponse {
  answer: string;
  actionList?: string[];
  status: number;
}

const httpClient = HttpClient.getInstance();
const splunkReporter = SplunkReporter.getInstance();

export default class Chat {
  private auth: Auth;
  private config: Config;
  private insight: Insight;

  constructor(auth: Auth, config: Config, insight: Insight) {
    this.auth = auth;
    this.config = config;
    this.insight = insight;
  }

  async fetchChatAnswer({
    userPrompt,
    enabledPaymentMethodList,
    isPartialPaymentEnabled,
    browser,
    device,
    isAutopayEligible,
    isAutopaySetup,
    hasAchConvenienceFee,
    achConvenienceFee,
    merchantName,
  }: ChatAnswerRequest): Promise<ChatAnswerResponse> {
    const { ticket, realmId, entityId, authToken } = this.auth;
    const { portal } = this.config;

    const requestHeaders = {
      Accept: 'application/json, text/javascript, */*; q=0.01',
      'Intuit-DomainId': this.insight.domainId,
      'Intuit-IntuitId': entityId,
      'Intuit-RealmId': realmId,
      'Intuit-ACSToken': this.insight.token,
      sessionTicket: ticket,
      Authorization: `Bearer ${authToken}`,
    };

    const url = `/${portal}/rest/chat/fetchChatAnswerQuestion`;
    const endpoint = '/rest/chat/fetchChatAnswerQuestion';
    const request = {
      url,
      method: 'POST' as const,
      data: {
        userPrompt,
        enabledPaymentMethodList,
        isPartialPaymentEnabled,
        browser,
        device,
        isAutopayEligible,
        isAutopaySetup,
        hasAchConvenienceFee,
        achConvenienceFee,
        merchantName,
      },
      headers: requestHeaders,
      endpoint,
      event: 'fetchChatAnswerQuestion',
    };

    try {
      const response = await httpClient(request);
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error: any) {
      splunkReporter.contextual({
        logInfo: { logger: 'businessLogic/Chat', logLevel: 'error' },
        event: 'fetchChatAnswerQuestion',
        activityInfo: {
          feature: 'aiAssist',
        },
        error: {
          message: error.message,
          status: error.response?.status,
        },
      });
      throw error;
    }
  }
}
